import { ExternalLink, Github, Play, Check, Cpu, Radio, Map, Navigation, Gauge, Car } from "lucide-react";
import lidarImage from "./images/LIDAR.jpg";
import roboticsImage from "./images/ROBOTICS.jpg";
import racerImage from "./images/Roboracer.jpg";

const projects = [
  {
    title: "Real-Time LiDAR Road & Sidewalk Detection",
    category: "Perception · Published research",
    image: lidarImage,
    icon: Radio,
    challenge: "Urban autonomous navigation needs reliable drivable-surface detection while processing high-throughput LiDAR data in real time.",
    solution: "Developed a LiDAR-centric road and sidewalk detection approach integrated into ROS 2, with an emphasis on computational efficiency and precise perception.",
    technologies: ["ROS 2", "LiDAR", "C++", "PCL", "Linux"],
    results: ["Real-time surface detection", "ROS 2 integration", "Peer-reviewed publication"],
    link: "https://doi.org/10.3390/engproc2025113024",
    linkLabel: "Read paper",
  },
  {
    title: "RoboRacer SLAM Evaluation",
    category: "Autonomous racing · IEEE IV 2025",
    image: racerImage,
    icon: Car,
    challenge: "Small-scale autonomous racing requires consistent maps and localization from limited onboard sensing in fast, constrained environments.",
    solution: "Evaluated SLAM Toolbox and Cartographer offline using real RoboRacer data, comparing generated occupancy maps against track ground truth.",
    technologies: ["ROS 2", "SLAM", "LiDAR", "RoboRacer", "RViz"],
    results: ["Quantitative SLAM comparison", "Real-world sensor dataset", "2nd place team at IEEE IV 2025"],
    link: "https://doi.org/10.3390/engproc2025113009",
    linkLabel: "Read paper",
  },
  {
    title: "Lexus RX450 ROS 2 Visualization",
    category: "Research vehicle · Developer tooling",
    image: roboticsImage,
    icon: Gauge,
    challenge: "A complex autonomous research vehicle produces many ROS 2 topics that engineers need to inspect quickly during development and testing.",
    solution: "Built a GUI for visualizing ROS 2 topics on the Lexus RX450 research platform, making live vehicle data easier to understand and diagnose.",
    technologies: ["ROS 2", "Python", "Qt", "Foxglove", "GUI"],
    results: ["Live topic visualization", "Faster system inspection", "Research-vehicle integration"],
  },
  {
    title: "ROS 1 to ROS 2 Algorithm Migration",
    category: "Robotics infrastructure",
    image: roboticsImage,
    icon: Cpu,
    challenge: "Autonomous-driving algorithms built around ROS 1 interfaces needed to move to the ROS 2 execution and communication model.",
    solution: "Translated research algorithms to ROS 2 and adapted their nodes, topics, and runtime integration for the newer robotics stack.",
    technologies: ["ROS 1", "ROS 2", "C++", "Python", "Linux"],
    results: ["ROS 2-compatible algorithms", "Modernized research stack", "Reusable autonomy components"],
  },
  {
    title: "Face Recognition Attendance System",
    category: "Computer vision · Desktop application",
    image: roboticsImage,
    icon: Cpu,
    challenge: "Manual attendance processes consume HR time and make accurate employee tracking difficult.",
    solution: "Built a desktop system that uses face recognition to record and analyze attendance, pairing an operator interface with a data workflow.",
    technologies: ["Python", "OpenCV", "Tkinter", "Pandas", "NumPy"],
    results: ["Automated identity matching", "Attendance data workflow", "HR-focused desktop UI"],
    link: "https://github.com/FaceRecognition-Attendance-System/Face-Recongition-Gui",
    linkLabel: "View source",
  },
  {
    title: "Fanni Jo Service Marketplace",
    category: "Full-stack engineering",
    image: roboticsImage,
    icon: Map,
    challenge: "Clients needed a simple way to discover suitable local service providers without relying on fragmented social-media searches.",
    solution: "Created a web marketplace connecting clients and providers through a Django backend and React interface.",
    technologies: ["Django", "Python", "React", "JavaScript", "Bootstrap"],
    results: ["End-to-end marketplace", "Client-provider discovery", "Deployed web interface"],
    link: "https://github.com/Fanni-Jo",
    linkLabel: "View source",
  },
  {
    title: "Autonomous Robotics Mentoring",
    category: "Education · Applied autonomy",
    image: racerImage,
    icon: Navigation,
    challenge: "High-school teams needed to turn autonomous-car concepts into reliable competition-ready behavior within a short program.",
    solution: "Mentored Hungarian students as they developed and optimized autonomous cars for a competition at Széchenyi István University.",
    technologies: ["Python", "Autonomous cars", "Teaching", "Testing"],
    results: ["Competition-ready vehicles", "Hands-on engineering mentorship", "One-day university event"],
  },
];

function ArchitectureDiagram() {
  const nodes = ["LiDAR", "ROS 2", "Perception", "Localization", "Nav2", "Motor control", "Robot"];
  return (
    <div className="architecture-panel" aria-label="Autonomous robot software architecture">
      <div className="architecture-heading"><span>System architecture</span><strong>Sensor to motion</strong></div>
      <div className="architecture-flow">
        {nodes.map((node, index) => (
          <div className="architecture-node-wrap" key={node}>
            <div className="architecture-node">{node}</div>
            {index < nodes.length - 1 && <span className="architecture-arrow">→</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Projects() {
  return (
    <section id="projects" className="projects-section background-alt">
      <div className="container" data-aos="fade-up">
        <div className="section-title section-title-left">
          <span className="section-index">04 — Selected work</span>
          <h2>Robots, research, and production-minded software</h2>
          <p>Case studies showing the problem, engineering approach, stack, and delivered outcome.</p>
        </div>
        <ArchitectureDiagram />
        <div className="case-study-list">
          {projects.map((project, index) => {
            const Icon = project.icon;
            return (
              <article className="case-study" key={project.title}>
                <div className="case-study-media">
                  <img src={project.image} alt="" />
                  <div className="case-study-overlay"><Icon size={22} /><span>{project.category}</span></div>
                  <div className="media-ready"><Play size={16} fill="currentColor" /> Project visual</div>
                </div>
                <div className="case-study-content">
                  <span className="case-number">{String(index + 1).padStart(2, "0")}</span>
                  <h3>{project.title}</h3>
                  <div className="case-detail"><strong>Challenge</strong><p>{project.challenge}</p></div>
                  <div className="case-detail"><strong>Solution</strong><p>{project.solution}</p></div>
                  <div className="tech-list">{project.technologies.map((tech) => <span key={tech}>{tech}</span>)}</div>
                  <ul className="results-list">{project.results.map((result) => <li key={result}><Check size={16} />{result}</li>)}</ul>
                  {project.link && (
                    <a className="project-action" href={project.link} target="_blank" rel="noopener noreferrer">
                      {project.linkLabel === "View source" ? <Github size={17} /> : <ExternalLink size={17} />} {project.linkLabel}
                    </a>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
